/**
 * Encodes an object into a query string.
 * @param obj - The object to encode into a query string.
 * @param [pfx] - An optional prefix to add before the query string.
 * @returns The encoded query string.
 * @example
 * ```
 * // Example input: encode({ token: 'foo', key: 'value' })
 * // Expected output: "token=foo&key=value"
 * ```
 */
function encode(obj, pfx) {
	let k,
		i,
		tmp,
		str = '';

	for (k in obj) {
		if ((tmp = obj[k]) !== void 0) {
			if (Array.isArray(tmp)) {
				for (i = 0; i < tmp.length; i++) {
					if (str) str += '&';
					str += `${k}=${tmp[i]}`;
				}
			} else {
				if (str) str += '&';
				str += `${k}=${tmp}`;
			}
		}
	}

	return (pfx || '') + str;
}

/**
 * Converts a string value to its appropriate type (string, number, boolean).
 * @param mix - The string value to convert.
 * @returns The converted value.
 * @example
 * // Example input: toValue("123")
 * // Expected output: 123
 */
function toValue(mix) {
	if (!mix) return '';
	const str = mix;

	if (str === 'false') return false;
	if (str === 'true') return true;
	return Number(str) * 0 === 0 && `${Number(str)}` === str
		? Number(str)
		: str;
}

/**
 * Decodes a query string into an object.
 * @param str - The query string to decode.
 * @param [pfx] - An optional prefix to filter out from the query string.
 * @returns The decoded key-value pairs in an object format.
 * @example
 * // Example input: decode("token=foo&key=value")
 * // Expected output: { "token": "foo", "key": "value" }
 */
export function decode(str, pfx) {
	let tmp, k;
	const out = {},
		arr = (pfx ? str.substr(pfx.length) : str).split('&');

	while ((tmp = arr.shift())) {
		const equalIndex = tmp.indexOf('=');
		if (equalIndex !== -1) {
			k = tmp.slice(0, equalIndex);
			const value = tmp.slice(equalIndex + 1);
			if (out[k] !== void 0) {
				out[k] = [].concat(out[k], toValue(value));
			} else {
				out[k] = toValue(value);
			}
		} else {
			k = tmp;
			out[k] = '';
		}
	}

	return out;
}

/**
 *
 * @param parser
 */
export function parseSearchWith(parser) {
	return (searchStr) => {
		if (searchStr.substring(0, 1) === '?') {
			searchStr = searchStr.substring(1);
		}

		const query = decode(searchStr);

		// Try to parse any query params that might be json
		for (const key in query) {
			if (Object.hasOwn(query, key)) {
				const value = query[key];
				if (typeof value === 'string') {
					try {
						query[key] = parser(value);
					} catch {
						//
					}
				}
			}
		}

		return query;
	};
}

/**
 *
 * @param stringify
 * @param parser
 */
export function stringifySearchWith(stringify, parser) {
	/**
	 *
	 * @param val
	 */
	function stringifyValue(val) {
		if (typeof val === 'object' && val !== null) {
			try {
				return stringify(val);
			} catch {
				// silent
			}
		} else if (typeof val === 'string' && typeof parser === 'function') {
			try {
				// Check if it's a valid parseable string.
				// If it is, then stringify it again.
				parser(val);
				return stringify(val);
			} catch {
				// silent
			}
		}
		return val;
	}

	return (search) => {
		search = { ...search };

		Object.keys(search).forEach((key) => {
			const val = search[key];
			if (typeof val === 'undefined' || val === undefined) {
				delete search[key];
			} else {
				search[key] = stringifyValue(val);
			}
		});

		const searchStr = encode(search).toString();

		return searchStr ? `?${searchStr}` : '';
	};
}

/**
 * Serializes an object into a custom string format
 * @param {object} obj - The object to serialize
 * @returns {string} The serialized string
 */
function serialize(obj) {
	const pairs = Object.entries(obj).map(([key, value]) => {
		const valueStr = Array.isArray(value)
			? value.map(encodeURIComponent).join('+')
			: encodeURIComponent(value);
		return `${encodeURIComponent(key)}:${valueStr}`;
	});

	return `(${pairs.join(',')})`;
}

/**
 * Parses a serialized string back into an object
 * @param {string} str - The string to parse
 * @returns {object} The parsed object
 */
function parse(str) {
	// Throw if doesn't start and end with parentheses
	if (!str.startsWith('(') || !str.endsWith(')')) {
		throw new Error('Invalid serialized string');
	}

	// Remove outer parentheses
	const content = str.slice(1, -1);

	// Split into key-value pairs
	const pairs = content.split(',');

	// Convert to object
	return pairs.reduce((obj, pair) => {
		const [key, value] = pair.split(':');

		if (key && value) {
			// Check if value contains '+' to determine if it should be an array
			obj[decodeURIComponent(key)] = value.includes('+')
				? value.split('+').map(decodeURIComponent)
				: decodeURIComponent(value);
		}

		return obj;
	}, {});
}

export const parseSearch = parseSearchWith(parse);
export const stringifySearch = stringifySearchWith(serialize, parse);

/**
 *
 * @param app
 */
export function useUrlParams(app) {
	app.ports.setUrlParam.subscribe(({ key, value, replace }) => {
		const search = parseSearch(window.location.search.substring(1));

		try {
			value = JSON.parse(value);
		} catch {
			// Ignore
		}

		if (value) {
			search[key] = value;
		} else {
			delete search[key];
		}

		const url =
			window.location.pathname +
			stringifySearch(search) +
			window.location.hash;

		if (replace) {
			window.history.replaceState(search, null, url);
		} else {
			window.history.pushState(search, null, url);
		}
	});

	// app.ports.setUrlParams.subscribe(({ params, replace }) => {
	// 	const url = new URL(window.location.href);

	// 	if (params.length === 0) {
	// 		return;
	// 	}

	// 	const newState = { ...window.history.state };
	// 	for (const { key, value } of params) {
	// 		const encodedValue = value ? encodeURIComponent(value) : null;

	// 		if (encodedValue) {
	// 			url.searchParams.set(key, encodedValue);
	// 			newState[key] = encodedValue;
	// 		} else {
	// 			url.searchParams.delete(key);
	// 			delete newState[key];
	// 		}
	// 	}

	// 	if (replace) {
	// 		window.history.replaceState(newState, null, url);
	// 	} else {
	// 		window.history.pushState(newState, null, url);
	// 	}
	// });

	window.addEventListener('popstate', () => {
		const params = [];
		const search = parseSearch(window.location.search.substring(1));
		for (const key in search) {
			if (search[key]) {
				params.push({ key, value: search[key] });
			}
		}

		app.ports.urlParamsChanged.send(params);
	});
}
